import * as React from 'react';
import { toast } from 'sonner';
import {
  ASSET_CONDITIONS,
  ASSET_CONDITION_LABELS,
  missingAccessories,
  requiresDeviceCredential,
  today,
  type AssetCondition,
} from '@cfl/assets-shared';

import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/components/cargofl-ui';
import { AccessoryPicker } from '@/components/accessory-picker';
import { PhotoPicker, uploadPendingPhotos } from '@/components/photo-picker';
import { AssetPicker } from '@/components/pickers';
import { PageContainer } from '@/components/shell/page-container';
import { ApiError } from '@/lib/api';
import { useAssetCategories, useAssets, type AssetSummary } from '@/lib/assets-api';
import { useReturnAsset } from '@/lib/operations-api';

/**
 * Enough of an asset to record its return against.
 *
 * `accessories` is what went out on the OPEN assignment, and it is the reason this is a
 * named type rather than an AssetSummary. The detail endpoint does not send that field
 * at the top level, so a caller there has to read it off the open assignment - handing
 * over `asset.accessories` from a detail record would silently produce an empty
 * checklist and record every charger as returned without anyone being asked.
 */
export type ReturnTarget = Pick<
  AssetSummary,
  // categoryId and ownership are what requiresDeviceCredential reads: a company laptop
  // must not be returnable without its login, and an employee's own device must never
  // be asked for one.
  'id' | 'code' | 'name' | 'holder' | 'categoryId' | 'ownership'
> & {
  accessories: string[];
  assignedAt: string | null;
};

/**
 * The return form itself, shared by the Return page and the row action on the register
 * and the asset detail screen.
 *
 * `asset` set means it was opened against one row: there is no picker, the footer is a
 * dialog footer, and a recorded return closes the dialog rather than resetting.
 */
function ReturnForm({
  asset,
  assets,
  onDone,
}: {
  asset?: ReturnTarget;
  /** The choosable ASSIGNED assets. Only read when there is a picker. */
  assets?: AssetSummary[];
  /** Cancel, and - in a dialog only - a recorded return. */
  onDone?: () => void;
}) {
  const inDialog = asset !== undefined;
  const recordReturn = useReturnAsset();

  const [assetId, setAssetId] = React.useState(asset?.id ?? '');
  const [returnedAt, setReturnedAt] = React.useState(today());
  const [condition, setCondition] = React.useState<AssetCondition>('GOOD');
  const [reason, setReason] = React.useState('');
  const [remarks, setRemarks] = React.useState('');
  const [returnedAccessories, setReturnedAccessories] = React.useState<string[]>([]);
  const [photos, setPhotos] = React.useState<File[]>([]);
  const [deviceUsername, setDeviceUsername] = React.useState('');
  const [devicePassword, setDevicePassword] = React.useState('');

  const selected = asset ?? assets?.find((a) => a.id === assetId);

  // Everything that went out starts ticked. Most returns are complete, so the
  // default is the common case and UNticking is the deliberate act that records a
  // loss - rather than making someone re-tick four boxes to say nothing went wrong.
  React.useEffect(() => {
    setReturnedAccessories(selected?.accessories ?? []);
  }, [assetId, selected?.accessories]);

  const missing = missingAccessories(selected?.accessories ?? [], returnedAccessories);

  /**
   * Whether this return has to surrender the machine's local login.
   *
   * The identical predicate the API rejects with - the SPA's copy only decides whether
   * to mark the fields required, and is a courtesy rather than a control.
   */
  const { data: categories } = useAssetCategories();
  const needsCredential = selected
    ? requiresDeviceCredential({
        ownership: selected.ownership,
        categoryRequiresCredentials:
          categories?.find((c) => c.id === selected.categoryId)?.requiresCredentials ?? false,
      })
    : false;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!assetId) {
      toast.error('Choose the asset being returned.');
      return;
    }

    try {
      const result = await recordReturn.mutateAsync({
        assetId,
        returnedAt,
        condition,
        reason: reason.trim() || null,
        remarks: remarks.trim() || null,
        returnedAccessories,
        // Sent only when the form actually asked for them, so a monitor's return does
        // not carry two empty strings the API then has to interpret.
        ...(needsCredential || deviceUsername.trim() !== ''
          ? {
              deviceUsername: deviceUsername.trim() || null,
              // NOT trimmed: a leading or trailing space is a legitimate character in a
              // password, and silently removing one stores a login that does not work.
              devicePassword: devicePassword || null,
            }
          : {}),
      });
      // Against the RETURN, kept apart from the handover photos on the same row. The
      // comparison between the two is the entire value: this is the lid now, that was
      // the lid when it went out.
      if (photos.length > 0) await uploadPendingPhotos('return', result.assignmentId, photos);

      const base =
        result.status === 'DAMAGED'
          ? `${result.assetCode} taken back from ${result.employeeName} and held back as damaged.`
          : `${result.assetCode} returned by ${result.employeeName} and back in the pool.`;
      // A missing charger is worth a warning toast rather than a success one - it is
      // the thing the person recording the return needs to go and chase.
      if (result.missingAccessories.length > 0) {
        toast.warning(`${base} Missing: ${result.missingAccessories.join(', ')}.`);
      } else {
        toast.success(base);
      }

      // Opened against one row: that asset is back now, so there is nothing left on
      // this form to submit.
      if (inDialog) {
        onDone?.();
        return;
      }

      setAssetId('');
      setReason('');
      setRemarks('');
      setCondition('GOOD');
      setPhotos([]);
      setDeviceUsername('');
      setDevicePassword('');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'That could not be recorded.');
    }
  };

  const submitButton = (
    <Button hint={"Record the return and the condition it came back in"} type="submit" disabled={recordReturn.isPending}>
      {recordReturn.isPending ? 'Recording…' : 'Record return'}
    </Button>
  );

  return (
    <form onSubmit={submit} className="space-y-4">
      {/* No picker when the asset is already settled — the dialog title names it. */}
      {!asset && (
        <div className="space-y-1.5">
          <Label>Asset</Label>
          {/* showHolder: on this screen you are usually looking for "the laptop
              Ravi has", not a code you already know. */}
          <AssetPicker assets={assets} value={assetId} onChange={setAssetId} showHolder />
        </div>
      )}

      {/* Read-only, and derived rather than chosen: the holder is a fact about
          the open assignment, and offering it as a dropdown would invite
          recording a return against the wrong person. */}
      {selected?.holder && (
        <div className="rounded-md bg-muted/60 p-3 text-sm">
          Currently held by <span className="font-medium">{selected.holder.name}</span>{' '}
          <span className="at-num text-muted-foreground">({selected.holder.employeeCode})</span>
          {selected.assignedAt && (
            <span className="text-muted-foreground"> since {selected.assignedAt}</span>
          )}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Date returned</Label>
          <Input
            type="date"
            value={returnedAt}
            max={today()}
            onChange={(e) => setReturnedAt(e.target.value)}
            required
          />
        </div>

        <div className="space-y-1.5">
          <Label>Condition it came back in</Label>
          <Select value={condition} onValueChange={(v) => setCondition(v as AssetCondition)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ASSET_CONDITIONS.map((c) => (
                <SelectItem key={c} value={c}>
                  {ASSET_CONDITION_LABELS[c]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Said before they submit, not after: this is the one choice on the form
          that changes where the asset ends up. */}
      <Alert className={condition === 'DAMAGED' ? 'border-warning/40 bg-warning/5' : undefined}>
        <AlertDescription className="text-xs">
          {condition === 'DAMAGED'
            ? 'Marked damaged, this will be held back rather than returned to the pool — so it cannot be issued to the next joiner by accident.'
            : 'This will go straight back into the pool and can be issued again immediately.'}
        </AlertDescription>
      </Alert>

      {/* Only shown when something actually went out with it - an empty
          checklist on a bare monitor is noise. */}
      {selected && selected.accessories.length > 0 && (
        <div className="space-y-2">
          <AccessoryPicker
            selected={returnedAccessories}
            onChange={setReturnedAccessories}
            // Exactly what was issued, and nothing else: ticking back a bag
            // that never went out would be inventing a record.
            options={selected.accessories}
            allowCustom={false}
            label="What came back"
            hint="Everything issued with this asset starts ticked. Untick anything that did not come back."
          />

          {missing.length > 0 && (
            <Alert className="border-warning/40 bg-warning/5">
              <AlertDescription className="text-xs leading-relaxed">
                <span className="font-medium">Not returned: {missing.join(', ')}.</span> This is
                recorded against the return and shows in the audit log — the asset still comes
                back, so it can be issued again.
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {/* Only for machines that have a login worth collecting, and never for an
          employee's own device. A password box in front of a returned chair is how
          people stop recording returns at all. */}
      {needsCredential && (
        <div className="space-y-2 rounded-md border border-primary/30 bg-primary/5 p-3">
          <Label>Login on this device</Label>
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            The account on the machine itself, so IT can start it up and check it before it
            goes out again. Not their Google or email password — that is never stored here.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <Input
              autoComplete="off"
              value={deviceUsername}
              onChange={(e) => setDeviceUsername(e.target.value)}
              placeholder="Account name"
            />
            <Input
              type="password"
              autoComplete="new-password"
              value={devicePassword}
              onChange={(e) => setDevicePassword(e.target.value)}
              placeholder="Password"
            />
          </div>
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Stored encrypted. Anyone who looks at it later is recorded in the audit log, and
            IT can delete it once the machine has been wiped.
          </p>
        </div>
      )}

      <PhotoPicker
        files={photos}
        onChange={setPhotos}
        label="Photos of what came back"
        hint="The state it is in now. Worth taking whenever the condition is anything but perfect — it is the evidence behind marking this damaged."
      />

      <div className="space-y-1.5">
        <Label>Reason for return</Label>
        <Input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Upgrade, resignation, role change…"
        />
      </div>

      <div className="space-y-1.5">
        <Label>Remarks</Label>
        <Textarea
          rows={2}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Charger missing. Dent on the corner."
        />
      </div>

      {inDialog ? (
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onDone}>
            Cancel
          </Button>
          {submitButton}
        </DialogFooter>
      ) : (
        submitButton
      )}
    </form>
  );
}

export function ReturnPage() {
  const { data: out } = useAssets({ status: 'ASSIGNED', pageSize: 1000 });
  const nothingOut = out !== undefined && out.assets.length === 0;

  return (
    <PageContainer>
      <Card className="max-w-3xl">
        <CardHeader>
          <CardTitle className="text-sm">Return an asset</CardTitle>
          <CardDescription className="text-xs">
            Only assets currently issued to someone appear here. The employee is whoever the
            register says is holding it — there is nothing to choose, and nothing to get wrong.
          </CardDescription>
        </CardHeader>

        <CardContent>
          {nothingOut ? (
            <Alert>
              <AlertDescription className="text-sm">
                Nothing is currently issued to anyone, so there is nothing to take back.
              </AlertDescription>
            </Alert>
          ) : (
            <ReturnForm assets={out?.assets} />
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}

/**
 * The same form, opened against one asset.
 *
 * Mounted only while it is open, so every open starts clean — there is no reset effect
 * to forget about the next time a field is added. The caller renders it conditionally.
 */
export function ReturnDialog({
  asset,
  onOpenChange,
}: {
  asset: ReturnTarget;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Return <span className="at-num">{asset.code}</span>
          </DialogTitle>
          <DialogDescription>
            {asset.name} — the condition it comes back in decides where it lands, so it is the
            one choice here worth pausing over.
          </DialogDescription>
        </DialogHeader>

        <ReturnForm asset={asset} onDone={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}
